import React from 'react';
import { Group, Text } from '@mantine/core';
import type { DeviceLatestPing } from '../../data/Device';

const LIVE_WINDOW_MS = 5 * 60 * 1000;

type Props = {
    latestPing: DeviceLatestPing | null | undefined;
};

const describePing = (ping: DeviceLatestPing): string => {
    if (ping.isCommercial) return 'Commercial break';
    if (ping.isSport && ping.currentSport) return ping.currentSport;
    if (ping.isSport) return 'Sport';
    return 'Idle';
};

const LiveBadge: React.FC<Props> = ({ latestPing }) => {
    if (!latestPing) {
        return (
            <Group gap={6} wrap="nowrap">
                <span
                    style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: 'var(--mantine-color-gray-5)',
                        display: 'inline-block',
                    }}
                />
                <Text size="xs" c="dimmed">
                    No pings yet
                </Text>
            </Group>
        );
    }

    const isLive = Date.now() - new Date(latestPing.createdAt).getTime() < LIVE_WINDOW_MS;

    if (!isLive) {
        const diffMins = Math.floor((Date.now() - new Date(latestPing.createdAt).getTime()) / 60_000);
        const ago =
            diffMins < 60
                ? `${diffMins}m ago`
                : diffMins < 60 * 24
                  ? `${Math.floor(diffMins / 60)}h ago`
                  : `${Math.floor(diffMins / 60 / 24)}d ago`;
        return (
            <Group gap={6} wrap="nowrap">
                <span
                    style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: 'var(--mantine-color-gray-5)',
                        display: 'inline-block',
                    }}
                />
                <Text size="xs" c="dimmed" truncate>
                    Last seen {ago}
                </Text>
            </Group>
        );
    }

    return (
        <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
            <span className="live-dot" aria-label="live" />
            <Text
                size="xs"
                fw={700}
                c="red.6"
                tt="uppercase"
                style={{ letterSpacing: '0.05em' }}
            >
                Live
            </Text>
            <Text size="xs" c="dimmed" truncate style={{ minWidth: 0 }}>
                {describePing(latestPing)}
            </Text>
        </Group>
    );
};

export default LiveBadge;
